/**
 * CypAdhdSsiDecisionTables — read-only summary of every SSI decision.
 *
 * Transcribed from 7_semiStructuredInterview/components/SSIDecisionSummaryTables.js
 * so the CYP review reads like the adult one: a fixed-layout table with a
 * primary-filled header, primary/5 rows, a primary/15 totals row per criterion
 * group, and the same tick / cross / em-dash decision icons.
 *
 * THE COLUMNS DIFFER, and must. The adult table splits every symptom into
 * "during adulthood" and "during childhood"; a child is IN childhood, so that
 * split carries no information here. The CYP columns are the questions the
 * clinician actually answers: has it persisted six months, was it there before
 * 12 (asked from 13 up), and in how many settings is it seen (Criterion C
 * needs two or more).
 *
 * There is no "Present" column: the generic presence determination was removed
 * on 26 Aug, since the two temporal answers ARE the determination — the same
 * shape as the adult panel, which asks presence once per life period rather
 * than once in the abstract. The per-criterion total therefore counts the
 * symptoms that persisted six months or more, which is what qualifies a
 * symptom toward Criterion A.
 *
 * Read-only by design: it is a place to check the decisions before generating,
 * not a second place to make them. Every value here is edited on its own
 * symptom page.
 */

import React from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/solid';
import { CYP_SSI_INCONCLUSIVE } from '../config/cypAdhdSsiConfig';

/**
 * Present / not present / not recorded, never colour alone.
 *
 * Exported because the CYP read-outs share one icon vocabulary: tick means
 * "recorded as present", cross means "recorded as absent", regardless of
 * whether present is good news clinically. The CI medical screening summary
 * (19_cypAdhdCI/components/CypAdhdMedicalScreeningSummary.js) reuses it so a
 * recorded Yes looks the same on every CYP table.
 */
export function DecisionIcon({ value, presentLabel = 'Yes', absentLabel = 'No' }) {
  if (value === true) {
    return <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" aria-label={presentLabel} />;
  }
  if (value === false) {
    return <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" aria-label={absentLabel} />;
  }
  if (value === CYP_SSI_INCONCLUSIVE) {
    return <span className="text-amber-800 text-xs font-semibold">Inconclusive</span>;
  }
  return <span className="text-gray-400 text-xs">Not recorded</span>;
}

function SymptomRows({ rows, totalLabel, total, groupKey, onsetAsked, afterTwelveAsked }) {
  return (
    <>
      {rows.map((row) => (
        <tr key={`${groupKey}-${row.dsmCode}`} className="bg-primary/5 border-b border-primary/10">
          <td className="p-3 text-sm text-center font-bold text-primary">{row.dsmCode}</td>
          <td className="p-3 text-xs">{row.name}</td>
          <td className="p-3 text-center bg-white">
            <DecisionIcon value={row.persisted} presentLabel="Present for six months or more" absentLabel="Not present for six months or more" />
          </td>
          {onsetAsked && (
            <td className="p-3 text-center bg-white">
              <DecisionIcon value={row.beforeTwelve} />
            </td>
          )}
          {afterTwelveAsked && (
            <td className="p-3 text-center bg-white">
              <DecisionIcon value={row.afterTwelve} presentLabel="Present after age 12" absentLabel="Not present after age 12" />
            </td>
          )}
          <td className="p-3 text-center bg-white text-xs text-gray-700">
            {row.settingsCount > 0 ? row.settingsCount : <span className="text-gray-400 text-xs">None</span>}
          </td>
        </tr>
      ))}
      <tr className="bg-primary/15 font-bold text-primary text-sm">
        <td className="p-3" colSpan="2">{totalLabel}</td>
        <td className="p-3 text-center">{total} / {rows.length}</td>
        <td className="p-3" colSpan={1 + (onsetAsked ? 1 : 0) + (afterTwelveAsked ? 1 : 0)}></td>
      </tr>
    </>
  );
}

export default function CypAdhdSsiDecisionTables({
  inattention = [],
  hyperactivity = [],
  domains = [],
  onsetAsked = true,
  // Onset-pair capture (TOC, TDI-205): adds the "After 12" column so the
  // review shows the recorded pair side by side.
  afterTwelveAsked = false,
  show = 'all',
}) {
  // 'all' keeps both tables in one block; 'symptoms' / 'impairment' render one
  // so the review page can put each behind its own disclosure, as the adult SSI
  // review does with "View Symptoms Decisions" / "View Impairment Decisions".
  // When scoped, the per-table <h4> is dropped: the disclosure's own label
  // already names it, and repeating it puts the same fact in two elements
  // visible at one glance.
  const showSymptoms = show !== 'impairment';
  const showImpairment = show !== 'symptoms';
  const isScoped = show !== 'all';
  // Counts the symptoms that COUNT: a symptom qualifies toward Criterion A by
  // having persisted six months or more, which is the answer the clinician now
  // records directly. There is no separate presence flag to count (26 Aug).
  const persistedCount = (rows) => rows.filter((r) => r.persisted === true).length;

  return (
    <div className="space-y-6 text-left">
      {showSymptoms && (
      <div>
        {!isScoped && (
          <h4 className="text-sm font-semibold text-gray-800 mb-2">Symptoms</h4>
        )}
        <table className="w-full text-left shadow-md rounded-lg overflow-hidden" style={{ tableLayout: 'fixed' }}>
          <caption className="sr-only">
            DSM-5 symptom decisions, with persistence, age of onset and the
            number of settings each symptom is seen in
          </caption>
          <colgroup>
            <col style={{ width: '9%' }} />
            <col style={{ width: afterTwelveAsked ? '43%' : '55%' }} />
            <col style={{ width: onsetAsked ? '14%' : '28%' }} />
            {onsetAsked && <col style={{ width: '14%' }} />}
            {afterTwelveAsked && <col style={{ width: '12%' }} />}
            <col style={{ width: '8%' }} />
          </colgroup>
          <thead>
            <tr className="bg-primary text-white">
              <th scope="col" className="p-3 text-center text-xs">Criterion</th>
              <th scope="col" className="p-3 text-xs">Symptom</th>
              {/* Under-13 the onset question is not asked (inside the age-12
                  window IS onset), so the one determination carries a merged
                  title rather than repeating in two columns. */}
              <th scope="col" className="p-3 text-center text-xs">
                {onsetAsked ? '6+ months' : '6+ months and before age 12'}
              </th>
              {onsetAsked && (
                <th scope="col" className="p-3 text-center text-xs">Before 12</th>
              )}
              {afterTwelveAsked && (
                <th scope="col" className="p-3 text-center text-xs">After 12</th>
              )}
              <th scope="col" className="p-3 text-center text-xs">Settings</th>
            </tr>
          </thead>
          <tbody>
            <SymptomRows
              rows={inattention}
              groupKey="ia"
              totalLabel="Inattention total"
              total={persistedCount(inattention)}
              onsetAsked={onsetAsked}
              afterTwelveAsked={afterTwelveAsked}
            />
            <SymptomRows
              rows={hyperactivity}
              groupKey="hi"
              totalLabel="Hyperactivity / impulsivity total"
              total={persistedCount(hyperactivity)}
              onsetAsked={onsetAsked}
              afterTwelveAsked={afterTwelveAsked}
            />
          </tbody>
        </table>
      </div>
      )}

      {showImpairment && domains.length > 0 && (
        <div>
          {!isScoped && (
            <h4 className="text-sm font-semibold text-gray-800 mb-2">
              Domains of Functional Impairment
            </h4>
          )}
          <table className="w-full text-left shadow-md rounded-lg overflow-hidden" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '70%' }} />
              <col style={{ width: '30%' }} />
            </colgroup>
            <thead>
              <tr className="bg-primary text-white">
                <th scope="col" className="p-3 text-xs">Domain of Functional Impairment</th>
                <th scope="col" className="p-3 text-center text-xs">At least moderate impairment</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((domain) => (
                <tr key={domain.key} className="bg-primary/5 border-b border-primary/10">
                  <td className="p-3 text-sm font-semibold text-primary">{domain.title}</td>
                  <td className="p-3 text-center bg-white">
                    <DecisionIcon
                      value={domain.impaired}
                      presentLabel="Affected"
                      absentLabel="Not affected"
                    />
                  </td>
                </tr>
              ))}
              <tr className="bg-primary/15 font-bold text-primary text-sm">
                <td className="p-3">Total domains impaired</td>
                <td className="p-3 text-center">
                  {domains.filter((d) => d.impaired === true).length} / {domains.length}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
